import React, { ReactElement } from 'react'
import { useTranslation } from 'next-i18next'
import { twJoin } from 'tailwind-merge'
import Head from 'next/dist/shared/lib/head'
import { FAVICON_LINK_KEYS } from 'shared/constants/favicon'
import { useRestrictedAccessNotification } from 'shared/hooks/use-restricted-access-notification'
import { DomainAssetsInterface } from 'shared/types/domain'
import Header from 'modules/header'
import { useBrand } from 'modules/white-label/contexts/brand-context'
import { useCourseData } from '../hooks/use-course-data'
import { useSetFonts } from '../hooks/use-set-fonts'
import { addPublicCustomClassNamePrefix } from '../utils/add-class-prefix'

interface Props extends React.PropsWithChildren {
  domainAssets?: DomainAssetsInterface
}

function CoursePageLayout({ children, domainAssets }: Props) {
  const { t } = useTranslation()
  useRestrictedAccessNotification()

  const { brand } = useBrand()
  const { courseData } = useCourseData()
  const { fontStyle, fontFamily } = useSetFonts()
  const publicCustomClassNamePrefix = addPublicCustomClassNamePrefix('course__app')
  const fontStyles = fontFamily ? `${fontFamily}, AvertaPE, sans-serif` : 'AvertaPE, sans-serif'

  return (
    <div
      className={twJoin('min-h-full bg-THEME-background', publicCustomClassNamePrefix)}
      style={{
        fontFamily: fontStyles,
      }}
    >
      <Head>
        <title>{courseData?.name || t('course.course_title')}</title>
        {domainAssets?.faviconUrl && (
          <link key={FAVICON_LINK_KEYS.icon} rel="icon" href={domainAssets.faviconUrl} />
        )}
        <style>{fontStyle}</style>
        <meta name="robots" content="noindex" />
      </Head>
      <Header
        logoSrc={courseData?.logo?.path || domainAssets?.logoUrl || brand.logo?.path}
        className="!bg-THEME-header-background font-[unset!important]"
        mobileSideMenuClassName="!bg-THEME-block-background"
        desktopMenuItemClassName="!text-THEME-header-text hover:!text-THEME-hover-header-text"
        desktopActiveMenuItemClassName="!text-THEME-hover-header-text"
        menuIconClassName="fill-THEME-header-text stroke-THEME-header-text hover:fill-THEME-hover-header-text hover:stroke-THEME-hover-header-text"
        activeMenuIconClassName="fill-THEME-hover-header-text stroke-THEME-hover-header-text"
        avatarLinkClassName="!text-THEME-primary hover:!text-THEME-hover-primary hover:!bg-transparent"
        avatarPopupClassName="!bg-THEME-block-background [&>hr]:text-THEME-disabled"
        mobileMenuItemClassName={`!text-THEME-primary hover:!text-THEME-hover-primary
          [&>svg]:!text-THEME-primary [&:hover>svg]:!text-THEME-hover-primary
        `}
        burgerClassName="[&>path]:stroke-THEME-header-text"
        languageSwitcherClassName={`!text-THEME-primary hover:!text-THEME-hover-primary 
          lg:!text-THEME-header-text lg:hover:!text-THEME-hover-header-text hover:!bg-transparent`}
        listLanguageSwitcherClassName="!bg-THEME-block-background"
        listLanguageSwitcherItemClassName={`!text-THEME-primary 
          hover:!text-THEME-hover-primary hover:!bg-THEME-block-background
        `}
      />
      <div
        className={`mx-auto max-w-[1747px] p-5 sm:px-6 md:px-5 ${addPublicCustomClassNamePrefix(
          'course__main',
        )}`}
      >
        {children}
      </div>
    </div>
  )
}

export function getCoursePageLayout(
  page: ReactElement,
  props: { domainAssets?: DomainAssetsInterface },
) {
  return <CoursePageLayout domainAssets={props.domainAssets}>{page}</CoursePageLayout>
}
